import { Box, Grid } from "@material-ui/core";
import Button from "components/form/flat-button";
import { EXCEL_FORMAT_TYPE, UploaderWithTips } from "components/upload/uploader-with-tips-view";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorWhileUploadingFile } from "./error-message-while-processing";
import { useFileSpecificationsAndTipsForUpload } from "./file-specifications-and-tips-for-upload";
import { IAttributeDataError } from "./types";
import { MAX_FILE_SIZE } from "./utils";

type UploaderFileToImportDoorProps = {
    handleDrop: (file: File, onSettled: (error: boolean) => void) => Promise<void>;
    errorMessages: IAttributeDataError;
    isUpdating: boolean;
    isUploading: boolean;
    wasProcessed: boolean;
    hasDataToChange: boolean;

    handlePressNextButton: () => void;
    clearValueStates?: () => void;
    handleClearErrors?: () => void;
};

export const UploaderFileToImportAttributes = ({
    errorMessages,
    handleDrop,
    hasDataToChange,
    isUpdating,
    isUploading,
    wasProcessed,
    handlePressNextButton,
    clearValueStates,
    handleClearErrors,
}: UploaderFileToImportDoorProps): JSX.Element => {
    const messageTips = useFileSpecificationsAndTipsForUpload();
    const [messageWhenNoHaveDataToChange, setMessageWhenNoHaveDataToChange] = useState<string>();

    useEffect(() => {
        if (!hasDataToChange && wasProcessed) {
            setMessageWhenNoHaveDataToChange(
                formatMessage("We couldn’t find any changes in your file. No doors or attributes have been modified.")
            );
        }
    }, [hasDataToChange, wasProcessed]);

    const getErrorMessage = useCallback((): React.ReactNode => {
        if (Object.keys(errorMessages ?? {}).length) {
            return <ErrorWhileUploadingFile errorMessages={errorMessages} />;
        }

        if (!hasDataToChange && wasProcessed && messageWhenNoHaveDataToChange) {
            return <p>{messageWhenNoHaveDataToChange}</p>;
        }

        return null;
    }, [errorMessages, hasDataToChange, messageWhenNoHaveDataToChange, wasProcessed]);

    const handleCallbackCloseAlert = () => {
        clearValueStates?.();
        handleClearErrors?.();
        setMessageWhenNoHaveDataToChange(undefined);
    };

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Box maxWidth="74.9rem" marginX="auto" paddingY={4}>
                    <UploaderWithTips
                        title={formatMessage(
                            "Upload a file to add new custom door grouping attributes, or modify existing values and door assignments."
                        )}
                        maxFileSize={MAX_FILE_SIZE}
                        formatType={EXCEL_FORMAT_TYPE}
                        onDrop={handleDrop}
                        tips={messageTips}
                        hasError={!!errorMessages}
                        errorMessage={getErrorMessage()}
                        isUploading={isUploading || isUpdating}
                        wasProcessed={wasProcessed}
                        hasDataToChange={hasDataToChange}
                        callbackCloseAlert={handleCallbackCloseAlert}
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box width="100%" display="flex" flexDirection="row-reverse">
                    <Button
                        key="next"
                        disabled={!hasDataToChange}
                        data-test={DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.IMPORT_DOOR_GROUPINGS.BUTTON_NEXT}
                        label={formatMessage("Next")}
                        primary={true}
                        onClick={handlePressNextButton}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};
